import {
  GetProductPriceCustomerTypeEnum,
  ProductPriceResponseDto,
  ConfigurationPriceResponseDto,
  RuntimeConfig,
  Environment,
} from '../types';
import { ConfigurationProductRequestDtoProductGroupEnum } from '../pls-store';

export type AvailableEnvironments =
  | 'test'
  | 'dev'
  | 'int'
  | 'pprod'
  | 'preprod'
  | 'prod'
  | 'DEV'
  | 'TEST'
  | 'INT'
  | 'PPROD'
  | 'PROD';

export enum ProductApiEnum {
  CONFIGURATION = 'CONFIGURATION',
  STOCK = 'STOCK',
}

export type TaxProfile = 'GROSS' | 'NET';
export type LoadingType = 'eager' | 'lazy';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'off';

export enum SeamlessDataStoreNames {
  PLS_CONNECTION_NAME = 'PLS_CONNECTION_V5',
  B2XCORE_AUTHENTICATION = 'B2XCORE_AUTHENTICATION',
  B2XCORE_PROFILE = 'B2XCORE_PROFILE',
  OWC_AEM_ENV = 'OWC_AEM_ENV',
  OWCC_REGION_PICKER_CONNECTION = 'DH-IO-OWCC_REGION_PICKER_CONNECTION',
}

export const PLS_CONNECTION_NAME = SeamlessDataStoreNames.PLS_CONNECTION_NAME;

export interface ProductPrice {
  value: number;
  formattedValue: string;
}

export type PriceDisplayType = 'purchase-price' | 'strike-price' | 'omnibus-price';

export interface ProductBadge {
  description?: string;
  footnote?: string;
  name?: string;
}

export interface TechnicalData {
  co2EmissionTotalCombined?: number;
  fuelConsumptionTotalCombined?: number;
  engine?: EngineRequest;
}

export interface EngineRequest {
  type: EngineRequestDtoTypeEnum;
  driveCycle: DriveCycle;
}

export enum EngineRequestDtoTypeEnum {
  ELECTRIC = 'ELECTRIC',
  HYBRID = 'HYBRID',
  COMBUSTOR = 'COMBUSTOR',
}

export interface DriveCycle {
  wltp?: Wltp;
}

export interface Wltp {
  emission?: WltpEmission;
  range?: WltpRange;
}

export interface WltpEmission {
  total?: number;
  weighted?: number;
}

export interface WltpRange {
  totalAll?: number;
  totalEquivalentAll?: number;
}

export enum DcpProductType {
  NCOS_D_STANDARD = 'NCOS_D_STANDARD',
  NCOS_B_STANDARD = 'NCOS_B_STANDARD',
  NCOS_D_COMVANS = 'NCOS_D_COMVANS',
  NCOS_B_COMVANS = 'NCOS_B_COMVANS',
}

export interface StockProduct {
  code: string;
  dcpProductType?: { name: DcpProductType };
  price?: ProductPrice;
  listPrice?: ProductPrice;
  badges?: ProductBadge[];
  productCategory?: string;
  isUsed?: boolean;
}

export interface ConfigurationProduct {
  vehicleId: string;
  offeringLocation?: string;
  productGroup: ConfigurationProductRequestDtoProductGroupEnum;
  basePriceAmount?: number;
  totalOptionPriceAmount?: number;
  configurationPriceAmount?: number;
  discountablePriceAmount?: number;
  technicalData?: TechnicalData;
}

export interface PriceDisplayConfigData {
  locale: Locale;
  taxProfile: TaxProfile;
  customerType: string;
  shouldUsePlsPurchasePrice: boolean;
  productType: string;
}

export type BreakdownType =
  | 'PURCHASE_PRICE'
  | 'ECO_BONUS'
  | 'ECO_BONUS_DE'
  | 'CUSTOMER_DISCOUNT'
  | 'BASE_PRICE'
  | 'STRIKE_PRICE'
  | 'SUB_TOTAL_PRICE'
  | 'TOTAL_PRICE'
  | 'VAN_TOTAL_PRICE'
  | 'PRICE_DISCOUNT'
  | 'PRICE_REDUCTION_VAN'
  | 'REDUCED_BASE_PRICE'
  | 'PRICE_REDUCTION'
  | 'TRANSFER_COSTS_ADDITIONAL';

export type BreakdownItemValueType = 'TOTAL_PRICE_VALUE';

export interface FootnoteGroup {
  type: BreakdownType | BreakdownItemValueType | AdditionalPriceInformationType;
  items: string[];
}

export type AdditionalPriceInformationType =
  | 'ECO_BONUS_ABSOLUTE'
  | 'ECO_BONUS_DE_ABSOLUTE'
  | 'STRIKE_PRICE'
  | 'PRICE_DISCOUNT'
  | 'LIST_PRICE';

export type PlsStatus = 'success' | 'error' | 'deactivated' | 'warning';

export interface PriceDisplayEventData {
  priceData: ProductPriceResponseDto | ConfigurationPriceResponseDto | undefined;
  product: StockProduct | ConfigurationProduct;
  displayConfig: PriceDisplayConfigData;
  footnotes?: FootnoteGroup[];
  plsStatus: PlsStatus;
  errorCode: string;
  errorMessage: string;
}

export type PlsUiEventData = {
  detail: [PriceDisplayEventData];
};

export interface PLSUIProps {
  locale?: Locale;
  customerType?: GetProductPriceCustomerTypeEnum;
  postCode?: string;
  freezeId?: string;
  freezeFallbackPrice?: string;
  environment?: Environment;
  configEnvironment?: Environment;
  loading?: LoadingType;
  taxProfile?: TaxProfile;
  logLevel?: LogLevel;
  customerWebToken?: string;
  customerGroup?: string;
  useCache?: boolean;
  darkMode?: boolean;
  footnoteModuleId?: string;
  templateConfig?: PLSUITemplateConfig;
  __overrideConfig?: RuntimeConfig;
  __overridePriceData?: ProductPriceResponseDto;
}

export type productResponseTypes =
  | 'priceBreakDown'
  | 'additionalPriceInformation'
  | 'taxProfile'
  | 'totalPrice'
  | 'purchasePriceLabel'
  | 'footnoteReferences'
  | 'footnotes';

export interface FootnotesItem {
  type: string;
  text: string;
}

export interface BreakDownItem {
  type: BreakdownType;
  amount?: number;
  label: string;
  value: string;
  class: string;
  text: string[];
}

export interface PhraseItem {
  label?: string;
  footnotes: {
    [key: string]: string;
  };
  value?: {
    footnotes: {
      [key: string]: string;
    };
  };
}

export interface PhraseData {
  priceBreakdown: {
    [key: string]: PhraseItem;
  };
  purchasePrice: {
    footnotes: {
      [key: string]: {
        [key: string]: {
          [key: string]: {
            [key: string]: string;
          };
        };
      };
    };
  };
  strikePrice: {
    footnotes: {
      [key: string]: {
        [key: string]: {
          [key: string]: string;
        };
      };
    };
  };
  additionalPriceInformation: {
    [key: string]: PhraseItem;
  };
  transferCosts: {
    footnotes: {
      [key: string]: string;
    };
  };
}

export enum BreakdownEvent {
  OPEN = 'breakdownOpen',
  CLOSE = 'breakdownClose',
}

export interface PLSUIStoreProductParams extends PLSUIProps {
  product: StockProduct | ConfigurationProduct;
  productType: ProductApiEnum;
}

export interface PLSUITemplateConfig {
  productType?: ProductApiEnum;
  product?: StockProduct | ConfigurationProduct;
  components?: [ComponentType];
  purchasePriceConfig?: PurchasePriceConfig;
}

export type ComponentType =
  | 'purchase-price'
  | 'omnibus-price'
  | 'transfer-cost'
  | 'additional-information'
  | 'standalone-inline-breakdown';

export interface PurchasePriceConfig {
  showStartFrom?: boolean;
  showTopLabel?: boolean;
  showStrikePrice?: boolean;
  showStrikePriceLabel?: boolean;
  showBreakdown?: boolean;
  breakdownConfig?: BreakdownConfig;
}

export interface BreakdownConfig {
  inline?: boolean;
  hostElement?: HTMLElement;
  openInModal?: boolean;
  darkMode?: boolean;
  useInfoPopupIcon?: boolean;
  closeOnClickOutside?: boolean;
}

export type Locale =
  | 'cs-CZ'
  | 'da-DK'
  | 'de-AT'
  | 'de-CH'
  | 'de-DE'
  | 'de-HU'
  | 'de-LU'
  | 'el-CY'
  | 'el-GR'
  | 'en-AE'
  | 'en-AU'
  | 'en-CH'
  | 'en-CN'
  | 'en-DE'
  | 'en-EG'
  | 'en-GB'
  | 'en-HK'
  | 'en-HU'
  | 'en-IE'
  | 'en-IN'
  | 'en-ID'
  | 'en-KR'
  | 'en-MX'
  | 'en-MY'
  | 'en-NZ'
  | 'en-SE'
  | 'en-SG'
  | 'en-TH'
  | 'en-TW'
  | 'en-VN'
  | 'en-ZA'
  | 'es-AR'
  | 'es-ES'
  | 'es-MX'
  | 'fi-FI'
  | 'fr-BE'
  | 'fr-CH'
  | 'fr-FR'
  | 'fr-LU'
  | 'hi-IN'
  | 'hu-HU'
  | 'it-CH'
  | 'it-IT'
  | 'ja-JP'
  | 'ko-KR'
  | 'nl-BE'
  | 'nl-NL'
  | 'no-NO'
  | 'pl-PL'
  | 'pt-BR'
  | 'en-BR'
  | 'pt-PT'
  | 'ro-RO'
  | 'ru-RU'
  | 'sk-SK'
  | 'sl-SI'
  | 'sr-RS'
  | 'sv-SE'
  | 'th-TH'
  | 'tr-TR'
  | 'tr-ZY'
  | 'vi-VN'
  | 'zh-CN'
  | 'zh-HK'
  | 'zh-TW';
